import type { BulkProgress } from './types'

type BulkProgressBarProps = {
  progress: BulkProgress
  /** Present-tense verb for the running action, e.g. `Sending` or `Applying`. */
  verb: string
  className?: string
}

/**
 * Live progress for a running bulk apply. The apply loop is one request per row,
 * so the bar tracks real work landing rather than faking a duration: it advances
 * as each contact finishes and tallies the ones that failed or were skipped.
 *
 * Deliberately separate from the app's other two progress indicators, which do
 * different jobs: `app/components/CircularProgressBar` is an indeterminate
 * loading spinner, and `app/components/ProgressBar` is a bare data bar for table
 * cells. This one is a labeled, accessible, determinate row for a running task.
 */
export function BulkProgressBar({ progress, verb, className }: BulkProgressBarProps) {
  const { done, total, failed, skipped, last } = progress
  const percent = total > 0 ? Math.round((done / total) * 100) : 0
  const tallies = [failed > 0 ? `${failed} failed` : ``, skipped > 0 ? `${skipped} skipped` : ``].filter(Boolean)

  return (
    <div className={`flex w-full flex-col gap-1 ${className ?? ``}`}>
      <div className="flex items-baseline justify-between gap-2 text-xs">
        <span className="font-medium text-default">
          {verb} {done} of {total}
        </span>
        {tallies.length > 0 && <span className="text-subtle">{tallies.join(` · `)}</span>}
      </div>
      <div
        role="progressbar"
        aria-valuenow={done}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-label={`${verb} ${done} of ${total}`}
        className="h-1.5 w-full overflow-hidden rounded-full bg-surface-muted"
      >
        <div
          className="h-full rounded-full bg-blue-600 transition-[width] duration-200 ease-out"
          style={{ width: `${percent}%` }}
        />
      </div>
      {/* Naming the row that just landed makes a long run feel like it's moving. */}
      <p className="h-4 truncate text-xs text-minimal">
        {last ? `${last.label}${!last.ok && last.reason ? ` · ${last.reason}` : ``}` : ``}
      </p>
    </div>
  )
}
